import React, { useMemo } from "react";
import InlineAlert from "@scripts/react/components/InlineAlert";
import { UploadLimitErrorType } from "@/constants/media";

interface UploadLimitErrorAlertProps {
    isOpen: boolean;
    onClose: () => void;
    errorType: UploadLimitErrorType | null;
    hideIcon?: boolean;
}

export const UploadLimitErrorAlert: React.FC<UploadLimitErrorAlertProps> = ({
    isOpen,
    onClose,
    errorType,
    hideIcon = false
}) => {
    const errorContent = useMemo(() => {
        switch (errorType) {
            case UploadLimitErrorType.TOO_MANY_IMAGES:
                return (
                    <p>
                        You can upload a <strong>maximum of 10 images</strong> at a time. Please
                        select up to 10 images and try again.
                    </p>
                );
            case UploadLimitErrorType.TOO_MANY_VIDEOS:
                return (
                    <p>
                        You can upload <strong>only 1 video</strong> at a time. Please select a
                        single video and try again.
                    </p>
                );
            case UploadLimitErrorType.MIXED_CONTENT:
                return (
                    <p>
                        You cannot upload images and videos together. Please select{" "}
                        <strong>up to 10 images</strong> or <strong>1 video</strong> and try again.
                    </p>
                );
            case UploadLimitErrorType.FILE_TOO_LARGE:
                return (
                    <p>
                        One or more files exceed the <strong>100MB size limit</strong>. Please
                        compress your files or select smaller files and try again.
                    </p>
                );
            default:
                return <p>An error occurred while uploading your files. Please try again.</p>;
        }
    }, [errorType]);

    if (!isOpen || !errorType) {
        return null;
    }

    return (
        <InlineAlert
            level="danger"
            type="primary"
            dense={true}
            textClassName="text-left"
            icon={hideIcon ? "hidden" : undefined}
            contentClassName={hideIcon ? "gap-0" : undefined}
        >
            {errorContent}
        </InlineAlert>
    );
};
